/**
 * EVENTSFLO-CLIENT-TAX-CODE-VALIDATION-MULTI-COUNTRY-V1
 *
 * Admin-facing tax-code validation panel mounted next to the VAT / Tax
 * Number field on ClientDetailPage. Wraps the country-aware
 * /validate-tax-code endpoint and lets the admin apply each suggested
 * update explicitly (never all-at-once, never automatic).
 */
import { useEffect, useState } from "react";
import {
  validateClientTaxCode,
  applyClientTaxValidationSuggestions,
  extractErrorMessage,
} from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, Loader2, Info } from "lucide-react";

const STATUS_META = {
  valid: { label: "Valid", color: "text-emerald-500", badge: "border-emerald-500/40" },
  invalid: { label: "Invalid", color: "text-red-500", badge: "border-red-500/40" },
  unavailable: { label: "Unavailable", color: "text-amber-500", badge: "border-amber-500/40" },
  manual_review: { label: "Manual review", color: "text-amber-500", badge: "border-amber-500/40" },
  error: { label: "Error", color: "text-red-500", badge: "border-red-500/40" },
};

const SOURCE_LABEL = {
  hmrc_uk_vat: "HMRC UK VAT",
  vies: "VIES",
  insee_sirene: "INSEE / Sirene (V2)",
  companies_house: "Companies House (V2)",
  manual_monaco: "Monaco manual review",
  unsupported_country: "Unsupported country — manual review",
  none: "No source",
};

function countryHelpText(cc) {
  const c = (cc || "").toUpperCase();
  if (c === "GB") {
    return "UK VAT validation uses HMRC when configured. A valid UK VAT result may suggest non-EU B2B services treatment, subject to configured Tax Rules Matrix rows.";
  }
  if (c === "FR") {
    return "French VAT numbers can be checked through VIES. SIREN/SIRET enrichment may require a separate official lookup.";
  }
  if (c === "MC") {
    return "Monaco client tax identity may require manual verification. Monaco clients normally remain domestic TVA.";
  }
  if (c) {
    return "EU VAT validation uses VIES. Name/address may not always be returned.";
  }
  return "Validation checks whether the VAT/tax number appears valid with an official source where available. It does not automatically change tax treatment.";
}

/**
 * @param {{
 *   clientId: string,
 *   vatCode?: string,
 *   billingCountry?: string,
 *   initialSnapshot?: object | null,
 *   onApplied?: (updatedClient: object) => void,
 * }} props
 */
export function ClientTaxCodeValidation({
  clientId,
  vatCode,
  billingCountry,
  initialSnapshot,
  onApplied,
}) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(initialSnapshot || null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setResult(initialSnapshot || null);
  }, [initialSnapshot, clientId]);

  const handleValidate = async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await validateClientTaxCode(clientId, {
        tax_code_override: vatCode || null,
        billing_country_override: (billingCountry || "").toUpperCase().slice(0, 2) || null,
        source_override: "auto",
        save_snapshot: true,
      });
      setResult(res.data);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const applyOne = async (payload) => {
    if (!clientId) return;
    setApplying(true);
    try {
      const res = await applyClientTaxValidationSuggestions(clientId, payload);
      toast.success("Applied selected update.");
      if (onApplied) onApplied(res.data);
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to apply update.");
    } finally {
      setApplying(false);
    }
  };

  const fields = result?.suggested_client_updates?.fields || {};
  const helperWarnings = result?.suggested_client_updates?.warnings || [];
  const meta = STATUS_META[result?.status] || null;

  return (
    <div className="border border-white/10 rounded-md bg-slate-950/40 p-3 mt-3 space-y-3" data-testid="client-tax-code-validation">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-medium text-foreground">
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
          Validate Tax Code
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleValidate}
          disabled={loading || !vatCode}
          data-testid="client-tax-code-validate-button"
        >
          {loading ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Validating…
            </>
          ) : (
            "Validate Tax Code"
          )}
        </Button>
      </div>

      <p className="text-[11px] text-muted-foreground leading-snug">
        {countryHelpText(billingCountry || result?.country_code)}
      </p>

      {error && (
        <Alert variant="destructive" className="py-2">
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      )}

      {result && (
        <>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div className="text-muted-foreground">Status</div>
            <div data-testid="client-tax-code-validation-status">
              <Badge variant="outline" className={`text-[10px] ${meta?.badge || "border-slate-500/40"} ${meta?.color || ""}`}>
                {meta?.label || result.status}
              </Badge>
            </div>
            <div className="text-muted-foreground">Source</div>
            <div className="font-mono text-[11px]" data-testid="client-tax-code-validation-source">
              {SOURCE_LABEL[result.source] || result.source}
            </div>
            <div className="text-muted-foreground">Country</div>
            <div className="font-mono text-[11px]" data-testid="client-tax-code-validation-country">
              {result.country_code || "—"}
            </div>
            <div className="text-muted-foreground">Number checked</div>
            <div className="font-mono text-[11px]" data-testid="client-tax-code-validation-number">
              {result.number_checked || "—"}
            </div>
            <div className="text-muted-foreground">Reference</div>
            <div className="font-mono text-[11px]" data-testid="client-tax-code-validation-reference">
              {result.reference || "—"}
            </div>
            <div className="text-muted-foreground">Checked at</div>
            <div className="font-mono text-[11px]">
              {result.checked_at ? new Date(result.checked_at).toLocaleString() : "—"}
            </div>
            {(result.registered_name || result.registered_address) && (
              <>
                <div className="text-muted-foreground">Registered name</div>
                <div className="text-[11px]">{result.registered_name || "—"}</div>
                <div className="text-muted-foreground">Registered address</div>
                <div className="text-[11px]">{result.registered_address || "—"}</div>
              </>
            )}
          </div>

          {(result.warnings?.length > 0 || helperWarnings.length > 0) && (
            <Alert className="border-amber-500/40 bg-amber-500/10 py-2" data-testid="client-tax-code-validation-warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="text-xs">Notes</AlertTitle>
              <AlertDescription className="text-[11px] leading-snug space-y-1">
                {(result.warnings || []).map((w, i) => (
                  <div key={`w-${i}`}>{w}</div>
                ))}
                {helperWarnings.map((w, i) => (
                  <div key={`h-${i}`}>{w}</div>
                ))}
              </AlertDescription>
            </Alert>
          )}

          {Object.keys(fields).length > 0 && (
            <div className="border-t border-white/5 pt-2 space-y-1.5">
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <Info className="h-3 w-3" />
                Suggested updates — apply each explicitly.
              </div>
              <div className="flex flex-wrap gap-2">
                {fields.vat_number_status && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={applying}
                    onClick={() => applyOne({ apply_vat_status: true })}
                    data-testid="client-tax-code-apply-vat-status"
                  >
                    Apply VAT status: {fields.vat_number_status}
                  </Button>
                )}
                {fields.client_entity_type && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={applying}
                    onClick={() => applyOne({ apply_entity_type: true })}
                    data-testid="client-tax-code-apply-entity-type"
                  >
                    Apply entity type: {fields.client_entity_type}
                  </Button>
                )}
                {fields.client_tax_treatment && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={applying}
                    onClick={() => applyOne({ apply_tax_treatment: true })}
                    data-testid="client-tax-code-apply-tax-treatment"
                  >
                    Apply tax treatment: {fields.client_tax_treatment}
                  </Button>
                )}
                {(fields.name || fields.billing_address) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={applying}
                    onClick={() => applyOne({ apply_company_details: true })}
                    data-testid="client-tax-code-apply-company-details"
                  >
                    Apply company details
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ClientTaxCodeValidation;
